import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { PhoneLogin } from "@/components/PhoneLogin";

const AuthCtx = createContext(null);

export const startLogin = (returnPath) => {
  window.dispatchEvent(new CustomEvent("moga-signin", { detail: returnPath }));
};

const SignInDialog = ({ setUser }) => {
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  useEffect(() => {
    const open = (e) => { setTarget(e.detail || window.location.pathname); };
    window.addEventListener("moga-signin", open);
    return () => { window.removeEventListener("moga-signin", open); };
  }, []);
  const done = (data) => {
    setUser(data);
    const to = target;
    setTarget(null);
    if (to && to !== window.location.pathname + window.location.search) navigate(to);
  };
  const onSuccess = async ({ credential }) => {
    try {
      const { data } = await api.post("/auth/google", { credential });
      done(data);
    } catch {
      toast.error("Sign-in failed. Please try again.");
    }
  };
  return (
    <Dialog open={target !== null} onOpenChange={(o) => !o && setTarget(null)}>
      <DialogContent className="max-w-sm rounded-3xl text-center" data-testid="signin-dialog">
        <DialogTitle className="font-display text-2xl font-extrabold text-ink">Moga 360</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">Sign in with Google or your mobile number · ਗੂਗਲ ਜਾਂ ਮੋਬਾਈਲ ਨੰਬਰ ਨਾਲ · गूगल या मोबाइल नंबर से</DialogDescription>
        <div className="mt-2 flex justify-center" data-testid="google-signin-button">
          <GoogleLogin onSuccess={onSuccess} onError={() => toast.error("Sign-in failed. Please try again.")}
            text="continue_with" shape="pill" size="large" width="280" />
        </div>
        <div className="my-1 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-line" />or<span className="h-px flex-1 bg-line" /></div>
        <PhoneLogin onDone={done} />
      </DialogContent>
    </Dialog>
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, refresh, logout }}>
      {children}
      <SignInDialog setUser={setUser} />
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
